import React from 'react';
import { Video as LucideIcon, TrendingUp, Calendar, Briefcase, UserCheck, Mail, Globe, Database, XCircle, Zap, AlertTriangle, Crosshair, Layers, ShieldAlert, Compass, RefreshCw } from 'lucide-react';
import { useAgentInsights } from '../../hooks/useAgentInsights';

type AgentType = 'call' | 'email' | 'linkedin' | 'whatsapp' | 'leadgen';

interface PerformanceIntelligenceProps {
  agentType: AgentType;
}

interface CardConfigItem {
  icon: LucideIcon;
  label: string;
  key: 'dataCard1' | 'dataCard2' | 'dataCard3' | 'aiInsight1' | 'aiInsight2' | 'aiInsight3';
}

const CARD_CONFIG: Record<AgentType, CardConfigItem[]> = {
  call: [
    { icon: Calendar, label: 'Best Day to Call', key: 'dataCard1' },
    { icon: Briefcase, label: 'Top Responding Industry', key: 'dataCard2' },
    { icon: UserCheck, label: 'Top Converting Title', key: 'dataCard3' },
    { icon: Zap, label: "What's Working", key: 'aiInsight1' },
    { icon: AlertTriangle, label: 'What to Watch', key: 'aiInsight2' },
    { icon: Crosshair, label: 'Top Opportunity', key: 'aiInsight3' },
  ],
  email: [
    { icon: Calendar, label: 'Best Day to Send', key: 'dataCard1' },
    { icon: Briefcase, label: 'Top Responding Industry', key: 'dataCard2' },
    { icon: Mail, label: 'Which Email Gets Reply', key: 'dataCard3' },
    { icon: Zap, label: "What's Working", key: 'aiInsight1' },
    { icon: AlertTriangle, label: 'What to Watch', key: 'aiInsight2' },
    { icon: Crosshair, label: 'Top Opportunity', key: 'aiInsight3' },
  ],
  linkedin: [
    { icon: Calendar, label: 'Best Day to Connect', key: 'dataCard1' },
    { icon: UserCheck, label: 'Top Responding Title', key: 'dataCard2' },
    { icon: Briefcase, label: 'Top Responding Industry', key: 'dataCard3' },
    { icon: Zap, label: "What's Working", key: 'aiInsight1' },
    { icon: AlertTriangle, label: 'What to Watch', key: 'aiInsight2' },
    { icon: Crosshair, label: 'Top Opportunity', key: 'aiInsight3' },
  ],
  whatsapp: [
    { icon: Calendar, label: 'Best Day to Message', key: 'dataCard1' },
    { icon: Globe, label: 'Top Responding Country', key: 'dataCard2' },
    { icon: Briefcase, label: 'Top Responding Industry', key: 'dataCard3' },
    { icon: Zap, label: "What's Working", key: 'aiInsight1' },
    { icon: AlertTriangle, label: 'What to Watch', key: 'aiInsight2' },
    { icon: Crosshair, label: 'Top Opportunity', key: 'aiInsight3' },
  ],
  leadgen: [
    { icon: Briefcase, label: 'Best Matching Industry', key: 'dataCard1' },
    { icon: Database, label: 'Data Completeness', key: 'dataCard2' },
    { icon: XCircle, label: 'Top Disqualification Reason', key: 'dataCard3' },
    { icon: Layers, label: 'ICP Fit Pattern', key: 'aiInsight1' },
    { icon: ShieldAlert, label: 'Data Quality Flag', key: 'aiInsight2' },
    { icon: Compass, label: 'Pipeline Recommendation', key: 'aiInsight3' },
  ],
};

function getTimeAgo(dateStr: string | null): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const SkeletonCard: React.FC = () => (
  <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl p-6 border-2 border-[#1875FF]/10 shadow-lg animate-pulse">
    <div className="flex flex-col items-center justify-center h-full space-y-4">
      <div className="w-14 h-14 bg-gray-200 rounded-xl" />
      <div className="space-y-2 w-full flex flex-col items-center">
        <div className="h-7 w-24 bg-gray-200 rounded" />
        <div className="h-4 w-32 bg-gray-100 rounded" />
      </div>
    </div>
  </div>
);

interface InsightCardProps {
  icon: LucideIcon;
  label: string;
  value: string;
  isAiInsight?: boolean;
}

const InsightCard: React.FC<InsightCardProps> = ({ icon: Icon, label, value, isAiInsight }) => {
  const hasValue = value && value !== '--';

  return (
    <div className="group bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl p-6 border-2 border-[#1875FF]/20 shadow-lg hover:shadow-2xl hover:border-[#05E0D1] transition-all duration-300 hover:-translate-y-1">
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <div
          className="w-14 h-14 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center group-hover:from-[#1875FF]/20 group-hover:to-[#05E0D1]/20 transition-all duration-300 group-hover:scale-110 shadow-md group-hover:shadow-lg"
          style={hasValue ? { animation: 'pulse-scale 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' } : undefined}
        >
          <Icon className="text-[#1875FF] group-hover:text-[#05E0D1] transition-colors opacity-100" size={26} />
        </div>
        <div className="space-y-1 text-center w-full">
          <div
            className={`font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1] transition-all duration-300 min-h-[1.75rem] ${
              isAiInsight ? 'text-sm leading-snug' : 'text-xl line-clamp-2'
            }`}
            title={isAiInsight && hasValue ? value : undefined}
          >
            {value || '--'}
          </div>
          <div className="text-sm font-semibold text-gray-700">{label}</div>
        </div>
      </div>
    </div>
  );
};

const PerformanceIntelligence: React.FC<PerformanceIntelligenceProps> = ({ agentType }) => {
  const insights = useAgentInsights(agentType);
  const { computedAt, isLoading, isRefreshing, refresh } = insights;
  const cards = CARD_CONFIG[agentType];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
            <TrendingUp className="text-white" size={22} />
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Performance Intelligence</h2>
        </div>
        <div className="flex items-center space-x-3">
          {computedAt && (
            <span className="text-xs text-gray-400">Last analyzed: {getTimeAgo(computedAt)}</span>
          )}
          <button
            onClick={refresh}
            disabled={isRefreshing}
            className="p-1.5 rounded-lg text-gray-400 hover:text-[#1875FF] hover:bg-[#1875FF]/5 transition-all duration-200 disabled:opacity-50"
            title="Refresh insights"
          >
            <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
          : cards.map((card) => (
              <InsightCard
                key={card.label}
                icon={card.icon}
                label={card.label}
                value={insights[card.key]}
                isAiInsight={card.key.startsWith('aiInsight')}
              />
            ))
        }
      </div>
    </div>
  );
};

export default PerformanceIntelligence;
